import React from "react";
import { Navigate } from "react-router-dom";
import RoleGuard from "../components/Common/RoleGuard";

// Authentication
import Login from "../pages/Authentication/Login";
import Logout from "../pages/Authentication/Logout";
import Register from "../pages/Authentication/Register";
import ForgetPwd from "../pages/Authentication/ForgetPassword";
import VerifyEmail from "../pages/Authentication/VerifyEmail";
import UserProfile from "../pages/Authentication/user-profile";
import AuthAction from "../pages/Authentication/AuthAction";

// PM — Support Portal pages
import SupportDashboard from "../pages/SupportPortal/Dashboard/index";
import TicketList from "../pages/SupportPortal/Tickets/TicketList";
import TicketDetail from "../pages/SupportPortal/Tickets/TicketDetail";
import TenantList from "../pages/SupportPortal/Tenants/TenantList";
import TenantDetail from "../pages/SupportPortal/Tenants/TenantDetail";
import AllTenantList from "../pages/SupportPortal/Tenants/AllTenantList";
import SupportMemberList from "../pages/SupportPortal/SupportMembers/SupportMemberList";
import PropertyList from "../pages/SupportPortal/Properties/PropertyList";
import PropertyDetail from "../pages/SupportPortal/Properties/PropertyDetail";
import AuditLog from "../pages/SupportPortal/Audit/AuditLog";
import Overview from "../pages/SupportPortal/Overview/index";
import ApplicationsPage from "../pages/SupportPortal/Applications/ApplicationsPage";
import PaymentsPage from "../pages/SupportPortal/Payments/PaymentsPage";
import InspectionsPage from "../pages/SupportPortal/Inspections/InspectionsPage";
import ChatsPage from "../pages/SupportPortal/Chats/ChatsPage";
import ListingsPage from "../pages/SupportPortal/Listings/ListingsPage";
import ListingInquiriesPage from "../pages/SupportPortal/Listings/ListingInquiriesPage";

// Admin pages
import AdminUserList from "../pages/Admin/AdminUserList";
import AdminContactMessages from "../pages/Admin/AdminContactMessages";

// Tenant pages
import TenantHome from "../pages/Tenant/TenantHome";
import TenantDashboard from "../pages/Tenant/TenantDashboard";
import TenantTickets from "../pages/Tenant/TenantTickets";
import TenantTicketDetail from "../pages/Tenant/TenantTicketDetail";
import TenantProperty from "../pages/Tenant/TenantProperty";
import TenantMyRentals from "../pages/Tenant/TenantMyRentals";
import TenantRentalDetail from "../pages/Tenant/TenantRentalDetail";

// Public pages
import LandingPage from "../pages/Landing/index";
import TermsOfService from "../pages/Legal/TermsOfService";
import PrivacyPolicy from "../pages/Legal/PrivacyPolicy";
import TenantScreeningPolicy from "../pages/Legal/TenantScreeningPolicy";
import FairHousingPolicy from "../pages/Legal/FairHousingPolicy";
import CommunityGuidelines from "../pages/Legal/CommunityGuidelines";
import PaymentsRefundPolicy from "../pages/Legal/PaymentsRefundPolicy";
import RentalApplicationPage from "../pages/Apply/RentalApplicationPage";
import MyApplicationsPage from "../pages/Apply/MyApplicationsPage";
import ContactPage from "../pages/Public/ContactPage";
import LandlordProfilePage from "../pages/Public/LandlordProfilePage";
import PublicTicketPage from "../pages/Public/PublicTicketPage";
import PricingPage from "../pages/Public/PricingPage";

// Shared (any authenticated user, regardless of role)
import NotificationsPage from "../pages/Notifications/NotificationsPage";

/**
 * PM-protected routes — require Firebase auth (any signed-in, verified user).
 * Wrapped with Authmiddleware in App.jsx. There's no PM approval step —
 * every user can switch into PM mode; creating a rental application link
 * is gated on having a Stripe payout method set up instead (see PropertyDetail.jsx).
 */
const pmProtectedRoutes = [
  { path: "/overview",  component: <Overview /> },
  { path: "/dashboard", component: <SupportDashboard /> },

  { path: "/tenants",                       component: <AllTenantList /> },
  { path: "/tenants/:portalId",             component: <TenantList /> },
  { path: "/tenants/:portalId/:tenantId",   component: <TenantDetail /> },

  { path: "/support-members", component: <SupportMemberList /> },

  { path: "/properties",             component: <PropertyList /> },
  { path: "/properties/:propertyId", component: <PropertyDetail /> },

  { path: "/applications", component: <ApplicationsPage /> },
  { path: "/payments",     component: <PaymentsPage /> },
  { path: "/inspections",  component: <InspectionsPage /> },
  { path: "/chats",        component: <ChatsPage /> },
  { path: "/listings",             component: <ListingsPage /> },
  { path: "/listings/:listingId",  component: <ListingInquiriesPage /> },

  { path: "/audit",   component: <AuditLog /> },
  { path: "/tickets", component: <TicketList /> },
  { path: "/tickets/:ticketId", component: <TicketDetail /> },

  // Admin
  { path: "/admin/users",      component: <AdminUserList /> },
  { path: "/admin/contact",    component: <AdminContactMessages /> },
];

/**
 * Role-gated routes — require only Firebase auth (no PM approval needed).
 * Tenants get here via invitation, not the PM signup flow.
 * Wrapped with plain Authmiddleware in App.jsx.
 */
const authProtectedRoutes = [
  // Applicant portal — any logged-in user can view their own applications
  { path: "/my-applications", component: <MyApplicationsPage /> },

  // Notification Center — shared by PMs and tenants alike
  { path: "/notifications", component: <NotificationsPage /> },

  { path: "/verify-email", component: <VerifyEmail /> },
  { path: "/profile", component: <UserProfile /> },

  // Tenant routes
  { path: "/tenant/home",       component: <TenantHome /> },
  { path: "/tenant/my-rentals", component: <RoleGuard allow={["tenant"]}><TenantMyRentals /></RoleGuard> },
  { path: "/tenant/my-rentals/:tenantRecordId", component: <RoleGuard allow={["tenant"]}><TenantRentalDetail /></RoleGuard> },
  { path: "/tenant/dashboard",  component: <RoleGuard allow={["tenant"]}><TenantDashboard /></RoleGuard> },
  { path: "/tenant/tickets",    component: <RoleGuard allow={["tenant"]}><TenantTickets /></RoleGuard> },
  { path: "/tenant/tickets/:ticketId", component: <RoleGuard allow={["tenant"]}><TenantTicketDetail /></RoleGuard> },
  { path: "/tenant/property",   component: <RoleGuard allow={["tenant"]}><TenantProperty /></RoleGuard> },
];

const publicRoutes = [
  { path: "/",                exact: true, component: <LandingPage /> },
  { path: "/logout",          component: <Logout /> },
  { path: "/login",           component: <Login /> },
  { path: "/forgot-password", component: <ForgetPwd /> },
  { path: "/auth/action",     component: <AuthAction /> },
  { path: "/register",        component: <Register /> },
  { path: "/contact",         component: <ContactPage /> },
  { path: "/pricing",         component: <PricingPage /> },
  { path: "/terms-of-service",        component: <TermsOfService /> },
  { path: "/privacy-policy",          component: <PrivacyPolicy /> },
  { path: "/tenant-screening-policy", component: <TenantScreeningPolicy /> },
  { path: "/fair-housing-policy",     component: <FairHousingPolicy /> },
  { path: "/community-guidelines",    component: <CommunityGuidelines /> },
  { path: "/payments-refund-policy",  component: <PaymentsRefundPolicy /> },
  { path: "/pm/:uid",            component: <LandlordProfilePage /> },
  { path: "/ticket/:ticketId",   component: <PublicTicketPage /> },
];

export { pmProtectedRoutes, authProtectedRoutes, publicRoutes };


